
import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Bot, User, Loader2 } from 'lucide-react';
import { getSolarAdvice } from '../services/geminiService';
import { ChatMessage } from '../types';

interface SolarAssistantProps {
  onClose: () => void;
}

const SolarAssistant: React.FC<SolarAssistantProps> = ({ onClose }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'model', text: 'Namaskara! I am the Sun-farms Solar Assistant. How can I help you save on electricity today?' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setIsLoading(true);

    const history = messages.map(m => ({
      role: m.role,
      parts: [{ text: m.text }]
    }));
    history.push({ role: 'user', parts: [{ text: userMessage }] });

    const response = await getSolarAdvice(history);
    setMessages(prev => [...prev, { role: 'model', text: response }]);
    setIsLoading(false);
  };

  return (
    <div className="fixed inset-0 sm:inset-auto sm:bottom-24 sm:right-6 sm:w-[400px] sm:h-[600px] bg-white sm:rounded-[2rem] shadow-2xl flex flex-col z-[60] overflow-hidden border border-slate-100 animate-in slide-in-from-bottom-8 duration-300">
      {/* Header */}
      <div className="bg-green-600 p-6 flex items-center justify-between text-white shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-xl">
            <Bot size={24} />
          </div>
          <div>
            <div className="font-bold">Sun-farms AI</div>
            <div className="text-xs text-green-100 flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-green-300 animate-pulse"></span>
              Expert Consultant
            </div>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
          <X size={24} />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className={`shrink-0 h-8 w-8 rounded-lg flex items-center justify-center ${msg.role === 'user' ? 'bg-green-600 text-white' : 'bg-white text-green-600 shadow-sm border border-slate-100'}`}>
                {msg.role === 'user' ? <User size={16} /> : <Bot size={16} />}
              </div>
              <div className={`p-4 rounded-2xl text-sm leading-relaxed shadow-sm ${msg.role === 'user' ? 'bg-green-600 text-white rounded-tr-none' : 'bg-white text-slate-700 rounded-tl-none border border-slate-100'}`}>
                {msg.text}
              </div>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="flex gap-3 items-center">
              <div className="h-8 w-8 rounded-lg bg-white border border-slate-100 flex items-center justify-center text-green-600">
                <Loader2 size={16} className="animate-spin" />
              </div>
              <div className="text-xs text-slate-400 font-medium italic">Assistant is thinking...</div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 bg-white border-t border-slate-100 shrink-0">
        <div className="flex gap-2">
          <input 
            type="text" 
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Ask about PM Surya Ghar subsidies..."
            className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-green-500 transition-all text-sm"
          />
          <button 
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="p-3 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 transition-all"
          >
            <Send size={20} />
          </button>
        </div>
        <div className="mt-2 text-[10px] text-center text-slate-400">
          Powered by Gemini 3.0 Flash Preview
        </div>
      </div>
    </div>
  );
};

export default SolarAssistant;
